<template>
  <div class="min-h-screen bg-[#27254A] flex items-center justify-center p-4">
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-10">
      <div
        class="absolute inset-0"
        style="
          background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23E472E1\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');
        "
      ></div>
    </div>

    <!-- Login Container -->
    <div class="relative z-10 w-full max-w-md">
      <!-- Logo Section -->
      <div class="text-center mb-8">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-black/30 backdrop-blur-sm rounded-2xl mb-4 border border-white/10"
        >
          <img
            src="/favicon.svg"
            alt="BioCorteX"
            class="w-16 h-16 object-contain"
          />
        </div>
        <h1 class="text-3xl font-bold text-white mb-2">BioCorteX</h1>
        <p class="text-gray-300 text-sm">Advanced Knowledge Graph Platform</p>
      </div>

      <!-- Login Card -->
      <div
        class="bg-black/30 backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 p-8"
      >
        <form class="space-y-6" @submit.prevent="handleLogin">
          <!-- Email Field -->
          <div>
            <label
              for="email"
              class="block text-sm font-medium text-gray-200 mb-2"
            >
              Email Address
            </label>
            <div class="relative">
              <input
                id="email"
                v-model="inputEmail"
                type="email"
                autocomplete="on"
                required
                class="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E472E1] focus:border-transparent transition-all duration-200"
                placeholder="name@biocortex.com"
              />
            </div>
          </div>

          <!-- Access Code Field -->
          <div>
            <label
              for="code"
              class="block text-sm font-medium text-gray-200 mb-2"
            >
              Access Code
            </label>
            <div class="relative">
              <input
                id="code"
                v-model="inputCode"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="on"
                required
                class="w-full px-4 py-3 pr-12 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E472E1] focus:border-transparent transition-all duration-200"
                placeholder="Enter your access code"
                @keyup.enter="handleLogin"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                @click="showPassword = !showPassword"
              >
                <svg
                  v-if="showPassword"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Error Message -->
          <div
            v-if="error"
            class="bg-red-500/20 border border-red-500/50 rounded-lg p-3"
          >
            <p class="text-red-200 text-sm text-center">{{ error }}</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-3 px-4 bg-[#e055dc] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-[#E472E1] focus:ring-offset-2 focus:ring-offset-[#27254A]"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Authenticating...
            </span>
            <span v-else>Access Platform</span>
          </button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center flex items-center justify-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            data-lucide="shield-check"
            class="lucide lucide-shield-check w-3 h-3 text-gray-400"
          >
            <path
              d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
            ></path>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
          <p class="text-gray-400 text-xs">
            Secure access to BioCorteX research platform
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";

const router = useRouter();
const inputEmail = ref("");
const inputCode = ref("");
const error = ref("");
const loading = ref(false);
const showPassword = ref(false);

async function handleLogin() {
  loading.value = true;
  error.value = "";

  try {
    // Send both email and token
    const response = await axios.post("/api/verify-access", {
      email: inputEmail.value.trim().toLowerCase(),
      token: inputCode.value.trim(),
    });

    // The backend returns a combined string "email|token"
    // We save THIS string as the 'bcx_auth_token'
    localStorage.setItem("bcx_auth_token", response.data.auth_string);

    router.push("/");
  } catch (err) {
    console.error(err);
    error.value = "Invalid Email or Access Code";
  } finally {
    loading.value = false;
  }
}
</script>
